import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { X, Link, Code, Globe, Check, User } from 'lucide-react';
import { LoadingSpinner } from '../LoadingSpinner';
import { useProjectContext } from '../../contexts/ProjectContext';
import { projectService } from '../../services/project.service';
import { useClickOutside } from '../../hooks/useClickOutside';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useNotifications } from '../../contexts/NotificationContext';
import { motion } from "framer-motion";
import { useAuth } from '../../hooks/useAuth';
import { SaveProjectDialog } from './SaveProjectDialog';
import { Project } from '../../types';

interface ShareProjectDialogProps {
    onClose: () => void;
}

type AccessLevel = 'No Access' | 'Can View' | 'Can Edit';
type CollaboratorAccessLevel = 'Can View' | 'Can Edit' | 'Remove';

interface Collaborator {
    email: string;
    role: 'viewer' | 'editor';
}

// This component is designed to be used with AnimatePresence in its parent component
export default function ShareProjectDialog({ onClose }: ShareProjectDialogProps) {
    const [publicAccess, setPublicAccess] = useState<AccessLevel>('No Access');
    const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
    const [isCopyingLink, setIsCopyingLink] = useState(false);
    const [isCopyingEmbed, setIsCopyingEmbed] = useState(false);
    const [isChangingAccess, setIsChangingAccess] = useState(false);
    const { project: currentProject, shareProject } = useProjectContext();
    const { showNotification } = useNotifications();
    const { isAuthenticated } = useAuth();
    
    const [internalShowSaveDialog, setInternalShowSaveDialog] = useState(false);
        
    // Refs for dialogs
    const mainContentRef = useClickOutside<HTMLDivElement>(onClose);
    const saveDialogWrapperRef = useRef<HTMLDivElement>(null);
    useEscapeKey(onClose); // Escape key always closes the entire dialog flow

    const isUnsavedProject = useMemo(() => 
        currentProject?.id === 'unsaved-project', 
        [currentProject?.id]
    );

    useEffect(() => {
        if (isUnsavedProject) {
            setInternalShowSaveDialog(true);
        } else {
            setInternalShowSaveDialog(false); // Ensure it's reset if project becomes saved
        }
    }, [isUnsavedProject]);

    const fetchCollaborators = useCallback(() => {
        if (currentProject && !isUnsavedProject && !internalShowSaveDialog) {
            const projectCollaborators: Collaborator[] = currentProject.shared_with.map(email => ({
                email,
                role: !isAuthenticated ? 'viewer' : 'viewer' 
            }));
            setCollaborators(projectCollaborators);
        }
    }, [currentProject, isUnsavedProject, isAuthenticated, internalShowSaveDialog]);
    
    useEffect(() => {
        fetchCollaborators();
    }, [fetchCollaborators]);
    
    useEffect(() => {
        if (!internalShowSaveDialog) { // Only fetch if not showing save dialog
            console.log('Share dialog content visible, refreshing collaborators');
            fetchCollaborators();
        }
    }, [internalShowSaveDialog, fetchCollaborators]);


    const handleSaveAndShowShareContent = useCallback(async (name: string, description: string) => {
        if (!currentProject) return;
        
        try {
            const updatedProject = { ...currentProject, name, description };
            // Assuming shareProject updates the project context, which will make isUnsavedProject false
            const result = await shareProject(updatedProject.id, ''); 
            
            if (result && typeof result === 'object' && 'message' in result) {
                throw new Error('Unexpected response when saving project');
            }
            
            const projectResult = result as Project;
            if (projectResult) {
                showNotification('success', 'Project saved successfully');
                setInternalShowSaveDialog(false); // Transition to share content
            }
        } catch (error) {
            console.error('Error saving project:', error);
            showNotification('error', 'Failed to save project');
        }
    }, [currentProject, shareProject, showNotification, setInternalShowSaveDialog]);

    const handleCopyLink = useCallback(async () => {
        if (!currentProject || internalShowSaveDialog) return;

        setIsCopyingLink(true);
        try {
            let previewId = currentProject.settings?.preview;
            if (!previewId) {
                const currentAccess = publicAccess;
                const result = await shareProject(currentProject.id, '');
                if (result && typeof result === 'object' && 'message' in result) {
                    throw new Error('Unexpected response when generating preview ID');
                }
                const projectResult = result as Project;
                if (!projectResult.settings?.preview) {
                    throw new Error('Failed to generate preview ID');
                }
                previewId = projectResult.settings.preview;
                if (currentAccess !== 'No Access') {
                    await projectService.setProjectPublicAccess(currentProject.id, currentAccess);
                    setPublicAccess(currentAccess);
                }
            }
            const shareUrl = `${window.location.origin}/projects/${currentProject.id}?preview=${previewId}`;
            await navigator.clipboard.writeText(shareUrl);
            showNotification('success', 'Share link copied to clipboard');
            setTimeout(() => setIsCopyingLink(false), 2000);
        } catch (error) {
            console.error('Error generating or copying share link:', error);
            showNotification('error', 'Failed to generate or copy share link');
            setIsCopyingLink(false);
        }
    }, [currentProject, shareProject, showNotification, publicAccess, internalShowSaveDialog]);

    const handleCopyEmbedCode = useCallback(async () => {
        if (!currentProject || internalShowSaveDialog) return;

        setIsCopyingEmbed(true);
        try {
            let previewId = currentProject.settings?.preview;
            if (!previewId) {
                const currentAccess = publicAccess;
                const result = await shareProject(currentProject.id, '');
                if (result && typeof result === 'object' && 'message' in result) {
                    throw new Error('Unexpected response when generating preview ID');
                }
                const projectResult = result as Project;
                if (!projectResult.settings?.preview) {
                    throw new Error('Failed to generate preview ID');
                }
                previewId = projectResult.settings.preview;
                if (currentAccess !== 'No Access') {
                    await projectService.setProjectPublicAccess(currentProject.id, currentAccess);
                    setPublicAccess(currentAccess);
                }
            }
            const embedUrl = `${window.location.origin}/projects/${currentProject.id}?preview=${previewId}&embed=true`;
            const embedCode = `<iframe src="${embedUrl}" width="100%" height="400" style="border: 1px solid #ccc;"></iframe>`;
            await navigator.clipboard.writeText(embedCode);
            showNotification('success', 'Embed code copied to clipboard');
            setTimeout(() => setIsCopyingEmbed(false), 2000);
        } catch (error) {
            console.error('Error generating or copying embed code:', error);
            showNotification('error', 'Failed to generate or copy embed code');
            setIsCopyingEmbed(false);
        }
    }, [currentProject, shareProject, showNotification, publicAccess, internalShowSaveDialog]);

    useEffect(() => {
        if (currentProject && !isUnsavedProject && !internalShowSaveDialog) {
            projectService.getProjectPublicAccess(currentProject.id)
                .then(level => {
                    if (!isAuthenticated && level === 'Can Edit') {
                        setPublicAccess('Can View');
                        projectService.setProjectPublicAccess(currentProject.id, 'Can View')
                            .catch(err => console.error('Error downgrading access level:', err));
                    } else {
                        setPublicAccess(level);
                    }
                })
                .catch(error => {
                    console.error('Error getting public access level:', error);
                    setPublicAccess('No Access');
                    if (!(error instanceof Error && error.message.includes('No authentication token available'))) {
                        showNotification('error', 'Failed to get public access level');
                    }
                });
        }
    }, [currentProject, isUnsavedProject, showNotification, isAuthenticated, internalShowSaveDialog]);

    const handlePublicAccessChange = useCallback(async (level: AccessLevel) => {
        if (!currentProject || internalShowSaveDialog) return;
        
        setIsChangingAccess(true);
        try {
            await projectService.setProjectPublicAccess(currentProject.id, level);
            setPublicAccess(level);
            showNotification('success', `Public access set to ${level}`);
        } catch (error) {
            console.error('Error updating public access:', error);
            showNotification('error', 'Failed to update public access');
        } finally {
            setIsChangingAccess(false);
        }
    }, [currentProject, showNotification, internalShowSaveDialog]);

    const handleCollaboratorAccessChange = useCallback(async (email: string, level: CollaboratorAccessLevel) => {
        if (!currentProject || internalShowSaveDialog) return;
        
        setIsChangingAccess(true);
        try {
            if (level === 'Remove') {
                await projectService.removeCollaborator(currentProject.id, email);
                setCollaborators(prev => prev.filter(c => c.email !== email));
                showNotification('success', `Removed ${email} from collaborators`);
            } else {
                const role = level === 'Can Edit' ? 'editor' : 'viewer';
                await projectService.removeCollaborator(currentProject.id, email);
                await projectService.addCollaborator(currentProject.id, email, role);
                setCollaborators(prev => 
                    prev.map(c => c.email === email ? { ...c, role } : c)
                );
                showNotification('success', `Updated ${email}'s access to ${level}`);
            }
        } catch (error) {
            console.error('Error updating collaborator access:', error);
            showNotification('error', 'Failed to update collaborator access');
        } finally {
            setIsChangingAccess(false);
        }
    }, [currentProject, showNotification, internalShowSaveDialog]);

    // Return the motion.div directly, similar to ConfirmDialog
    // AnimatePresence should be in the parent component
    return (
        <motion.div 
            className="fixed inset-0 bg-gray-950/50 dark:bg-gray-950/70 flex items-center justify-center z-[200]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
        >
            {internalShowSaveDialog ? (
                // Use SaveProjectDialog in non-standalone mode
                <motion.div 
                    ref={saveDialogWrapperRef}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                    <SaveProjectDialog
                        open={true} 
                        onClose={onClose} 
                        onSave={handleSaveAndShowShareContent} 
                        initialName={currentProject?.name}
                        headerText="Save Project Before Sharing"
                    />
                </motion.div>
            ) : (
                <motion.div 
                    ref={mainContentRef}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-[480px] mx-4 border border-gray-200 dark:border-gray-700"
                    initial={{ scale: 0.9, opacity: 0, y: 20 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.9, opacity: 0, y: 20 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                    {/* Header */}
                    <div className="px-6 pt-5 rounded-t-lg">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Share Project</h2>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-300 dark:text-gray-500 dark:hover:text-gray-400"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {/* Invite by email (commented out) */}
                        {/* Who has access */}
                        <div className="mb-6">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                Who has access
                            </h3>
                            <div className="flex flex-col border border-gray-200 dark:border-gray-700 rounded-md">
                                {/* Public access */}
                                <div className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 pl-3 pr-2 w-full">
                                    <div className="flex items-center gap-2">
                                        <Globe className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                                        <span className="text-sm text-gray-700 dark:text-gray-300">Anyone with the link</span>
                                    </div>
                                    <div className="flex items-center">
                                        {isChangingAccess && (
                                            <LoadingSpinner size="sm" className="mr-2 text-blue-500" />
                                        )}
                                        <select
                                            value={publicAccess}
                                            onChange={(e) => handlePublicAccessChange(e.target.value as AccessLevel)}
                                            disabled={isChangingAccess}
                                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent py-1 pl-2 pr-8"
                                        >
                                            <option>No Access</option>
                                            <option>Can View</option>
                                            {isAuthenticated && <option>Can Edit</option>}
                                        </select>
                                    </div>
                                </div>
                                {/* MetaRouter Team Access (commented out) */}
                                {/* Collaborators */}
                                {collaborators.map((collaborator, index) => (
                                    <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-gray-700 pl-3 pr-2 w-full">
                                        <div className="flex items-center gap-2">
                                            <div className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                                                <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <span className="text-sm text-gray-700 dark:text-gray-300">{collaborator.email}</span>
                                        </div>
                                        <select
                                            value={collaborator.role === 'editor' ? 'Can Edit' : 'Can View'}
                                            onChange={(e) => handleCollaboratorAccessChange(collaborator.email, e.target.value as CollaboratorAccessLevel)}
                                            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm rounded-md focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent py-1 pl-2 pr-8"
                                        >
                                            <option>Can View</option>
                                            {isAuthenticated && <option>Can Edit</option>}
                                            <option>Remove</option>
                                        </select>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Bottom bar */}
                        <div className="flex gap-3 mt-6">
                            <motion.button
                                onClick={handleCopyLink}
                                disabled={publicAccess === 'No Access'}
                                className={`
                                    px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2
                                    ${isCopyingLink
                                        ? 'bg-green-500 text-white'
                                        : publicAccess === 'No Access'
                                            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }
                                    transition-colors duration-300
                                `}
                                whileTap={publicAccess !== 'No Access' ? { scale: 0.95 } : undefined}
                            >
                                {isCopyingLink ? ( <> <Check className="h-4 w-4" /> Copied! </> ) : ( <> <Link className="h-4 w-4" /> Copy link </> )}
                            </motion.button>
                            <motion.button
                                onClick={handleCopyEmbedCode}
                                disabled={publicAccess === 'No Access'}
                                className={`
                                    px-4 py-2 text-sm font-medium rounded-md flex items-center gap-2
                                    ${isCopyingEmbed
                                        ? 'bg-green-500 text-white'
                                        : publicAccess === 'No Access'
                                            ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                            : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }
                                    transition-colors duration-300
                                `}
                                whileTap={publicAccess !== 'No Access' ? { scale: 0.95 } : undefined}
                            >
                                {isCopyingEmbed ? ( <> <Check className="h-4 w-4" /> Copied! </> ) : ( <> <Code className="h-4 w-4" /> Get embed code </> )}
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            )}
        </motion.div>
    );
}
